<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  positions: number[]
  showSetup: boolean
  loading?: boolean
}>()

const emit = defineEmits<{
  setPassphrase: [value: string]
  verifyLetters: [letters: string[]]
  changeWord: []
}>()

const { t } = useI18n()
const passphrase = ref('')
const showPass = ref(false)
const letter1 = ref('')
const letter2 = ref('')

const prompt = computed(() => {
  if (!props.positions.length) return ''
  const readable = props.positions.map((p) => `${t('auth.enter_letter')}${p}`).join(' & ')
  return t('auth.enter_letters_prompt', { letters: readable })
})

watch(
  () => props.positions,
  () => {
    letter1.value = ''
    letter2.value = ''
  },
)

function submitSetup() {
  emit('setPassphrase', passphrase.value.trim())
}

function submitVerify() {
  const letters: string[] = []
  if (props.positions.length >= 1) letters.push(letter1.value.trim())
  if (props.positions.length >= 2) letters.push(letter2.value.trim())
  emit('verifyLetters', letters)
}
</script>

<template>
  <div class="rounded-lg border border-dashed border-white-light p-4">
    <strong class="mb-2 block">{{ t('auth.step2_passphrase') }}</strong>

    <form v-if="showSetup" class="space-y-2" @submit.prevent="submitSetup">
      <label class="text-sm">{{ t('auth.passphrase_create') }}</label>
      <div class="flex gap-2">
        <input
          v-model="passphrase"
          :type="showPass ? 'text' : 'password'"
          class="form-input flex-1"
          autocomplete="new-password"
          minlength="4"
        />
        <button type="button" class="btn btn-secondary btn-sm" @click="showPass = !showPass">
          {{ showPass ? '🙈' : '👁' }}
        </button>
      </div>
      <button type="submit" class="btn btn-primary w-full" :disabled="loading">
        {{ t('auth.save_passphrase') }}
      </button>
    </form>

    <form v-else class="space-y-2" @submit.prevent="submitVerify">
      <p class="text-sm font-semibold">{{ prompt }}</p>
      <div class="grid grid-cols-2 gap-2">
        <input
          v-model="letter1"
          class="form-input text-center"
          maxlength="2"
          autocomplete="off"
          :placeholder="'#' + (positions[0] ?? 1)"
        />
        <input
          v-if="positions.length >= 2"
          v-model="letter2"
          class="form-input text-center"
          maxlength="2"
          autocomplete="off"
          :placeholder="'#' + (positions[1] ?? 2)"
        />
      </div>
      <div class="flex gap-2">
        <button type="submit" class="btn btn-primary w-full" :disabled="loading">
          {{ t('auth.check_letters') }}
        </button>
        <button type="button" class="btn btn-secondary" @click="emit('changeWord')">
          {{ t('auth.change_word') }}
        </button>
      </div>
    </form>
  </div>
</template>
